import { Typography } from '@mui/material';
import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction, PieceAssetSize, PieceAssetType } from '@apiContract';

import { apiRequest } from '@src/api/apiRequest';
import { uploadImageToS3 } from '@src/api/imageS3';
import { getUploadUrl } from '@src/api/piece';

import { ActionComponentProps } from '../../index';
import { buildValidationPieces, getItemsCount } from '../../utils';
import { DialogSignatureConfirmation } from '../DialogSignatureConfirmation';
import { ActionWithInfo } from './ActionWithInfo';

export const FinishDeliveryWithSignature = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  validationData,
}: ActionComponentProps) => {
  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const count = getItemsCount(mailpieces);
  const confirmAction = async (
    imageBase64: string,
    skipNotifyRecipient: boolean,
    comment: string,
    notifyMailroom: boolean,
  ) => {
    setActionInProgress(true);

    const { pieceAssets } = await getUploadUrl(mailpieces[0].id);

    const { url, key } =
      pieceAssets.find(
        (asset) =>
          asset.pieceAssetType === PieceAssetType.PickupSignature && asset.pieceAssetSize === PieceAssetSize.Large,
      ) || {};

    await apiRequest(uploadImageToS3, { image: imageBase64, uploadUrl: url, contentType: 'image/jpg' }, false, 5);

    await updatePieces({
      pieces: buildValidationPieces(mailpieces, validationData),
      action: PieceAction.FinishDeliveryWithSignature,
      pickupSignatureKey: key,
      shouldNotifyRecipient: !skipNotifyRecipient,
    }).unwrap();

    if (comment.trim().length) {
      await createMailpiecesListComment({
        text: comment.trim(),
        notifyMailroom,
        pieceIds: mailpieces.map((piece) => piece.id),
      }).unwrap();
    }

    setActionInProgress(false);
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title="Complete with signature"
      onClose={onActionFinish}
      validationData={validationData}
      acceptButtonTitle="Capture Signature"
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {count > 1 ? (
            <>
              Please ask the recipient to sign for <strong>{mailpieces.length}</strong> items.
            </>
          ) : (
            'Please ask the recipient to sign for this item.'
          )}
        </Typography>
      }
    >
      {({ step, skipNotifyRecipient, comment, notifyMailroom, onClose, onFinish }) => (
        <DialogSignatureConfirmation
          mailpieces={mailpieces}
          actionStarted={actionStarted && step === 'action'}
          persist={actionInProgress || isLoading}
          onActionFinish={onClose}
          onConfirm={async (imageBase64) => {
            await confirmAction(imageBase64, skipNotifyRecipient, comment, notifyMailroom);
            onFinish();
          }}
        />
      )}
    </ActionWithInfo>
  );
};
